import React from "react";
import PropTypes from "prop-types";
import ReactSelect, { Creatable } from "react-select";
import VirtualizedSelect from "react-virtualized-select";
import FaPlus from "react-icons/fa/plus";
import { categoryType, reduxFormInputType, selectOptionType } from "custom-prop-types";
import "components/virtualized-select.scss";

export const Select = ({ data, onChange, placeholder, value }) => (
  <VirtualizedSelect
    clearable={ false }
    onChange={ onChangeMapper(onChange) }
    options={ optionsMapper(data) }
    placeholder={ placeholder }
    value={ valueMapper(value) }
  />
);

export const valueMapper = (v) => {
  if (v === null || v === "" || v === undefined) {
    return null;
  }
  return { label: v.name, value: v.key, disabled: v.disabled, info: v.info };
};

export const optionsMapper = (data) => data.map(valueMapper);

export const onChangeMapper = (onChange) => ((data) => {
  const newData = { key: data.value, name: data.label, info: data.info };
  onChange(newData);
});

Select.propTypes = {
  data: PropTypes.arrayOf(selectOptionType).isRequired,
  onChange: PropTypes.func.isRequired, // (data: dataType) => void,
  placeholder: PropTypes.string,
  value: selectOptionType
};

Select.defaultProps = {
  placeholder: undefined,
  value: null
};

export const CreatableSelect = ({
  accountId,
  createOption,
  data,
  onChange,
  placeholder,
  value
}) => (
  <VirtualizedSelect
    clearable={ false }
    onChange={ onChangeMapper(onChange) }
    options={ optionsMapper(data) }
    placeholder={ placeholder }
    selectComponent={
      (props) => (
        <Creatable
          { ...props }
          onNewOptionClick={ (label) => { createOption(accountId, label.value); onChange({ name: label.value }); } }
          promptTextCreator={ (label) => `Add '${label}'` }
        />
      )
      }
    value={ valueMapper(value) }
  />
);

CreatableSelect.propTypes = Select.propTypes;
CreatableSelect.defaultProps = Select.defaultProps;

export const SimpleSelect = ({ data, onChange, placeholder, value }) => (
  <ReactSelect
    clearable={ false }
    onChange={ onChangeMapper(onChange) }
    options={ optionsMapper(data) }
    placeholder={ placeholder }
    searchable={ false }
    value={ valueMapper(value) }
  />
);

SimpleSelect.propTypes = {
  data: PropTypes.arrayOf(selectOptionType).isRequired,
  onChange: PropTypes.func.isRequired, // (data: dataType) => void
  placeholder: PropTypes.string,
  value: selectOptionType
};

SimpleSelect.defaultProps = {
  placeholder: "Select...",
  value: null
};

export const MultiSelect = (props) => (
  <ReactSelect
    { ...props }
    clearable={ false }
    multi
    onBlur={ () => props.input.onBlur([...props.input.value]) }
    onChange={ (value) => props.input.onChange(value) }
    options={ optionsMapper(props.options) }
    value={ props.input.value }
  />
);

MultiSelect.propTypes = {
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.arrayOf(selectOptionType), PropTypes.string])
  }),
  options: PropTypes.arrayOf(selectOptionType)
};

MultiSelect.defaultProps = {
  input: null,
  placeholder: "Select...",
  value: null
};

export const MultiCreatableSelect = (props) => {
  const optionsRemapper = (options) => options.map(option => ({ key: option.name, name: option.name }));
  const newOptions = optionsRemapper(props.options);
  return (
    <VirtualizedSelect
      { ...props }
      clearable={ false }
      multi
      onBlur={ () => props.input.onBlur([...props.input.value]) }
      onChange={ (value) => { props.input.onChange(value); } }
      options={ optionsMapper(newOptions) }
      placeholder={ props.placeholder }
      selectComponent={ selectComponentCreator }
      tabSelectsValue={ false }
      value={ props.input.value }
    />
  );
};

MultiCreatableSelect.propTypes = {
  input: reduxFormInputType.isRequired,
  options: PropTypes.arrayOf(categoryType).isRequired,
  placeholder: PropTypes.string.isRequired
};

const selectComponentCreator = (props) => (
  <Creatable
    { ...props }
    onNewOptionClick={
      (label) => {
        props.addFunction(props.accountId, label.value);
        props.input.onChange([...props.input.value, label.value]);
      }
    }
    promptTextCreator={ (label) => renderAddLabel(label) }
    shouldKeyDownEventCreateNewOption={ ({ keyCode }) => disableTab(keyCode) }
  />
);

selectComponentCreator.propTypes = {
  accountId: PropTypes.string.isRequired,
  addFunction: PropTypes.func.isRequired,
  input: reduxFormInputType.isRequired
};

export const CreatableMultiSelect = (props) => {
  const optionsRemapper = (options) => options.map(option => ({ key: option.name, name: option.name }));
  const newOptions = optionsRemapper(props.options);
  const valueRemapper = (values) => values.map((value) => value.value);
  return (
    <VirtualizedSelect
      { ...props }
      clearable={ false }
      multi
      onChange={ (value) => { props.onChange(props.field, valueRemapper(value)); } }
      options={ optionsMapper(newOptions) }
      placeholder={ props.placeholder }
      selectComponent={ selectCreator }
      tabSelectsValue={ false }
      value={ props.value }
    />
  );
};

CreatableMultiSelect.propTypes = {
  field: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(categoryType).isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string)
};

const selectCreator = (props) => (
  <Creatable
    { ...props }
    onNewOptionClick={
      (label) => {
        props.addFunction(props.accountId, label.value);
        props.onChange([...props.value, label.value]);
      }
    }
    promptTextCreator={ (label) => renderAddLabel(label) }
    shouldKeyDownEventCreateNewOption={ ({ keyCode }) => disableTab(keyCode) }
  />
);

selectCreator.propTypes = {
  accountId: PropTypes.string.isRequired,
  addFunction: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.string)
};


const disableTab = (keyCode) => keyCode === 13;

const renderAddLabel = (label) => (
  <span>
    <FaPlus size={ 15 } />
    {` Add '${label}'`}
  </span>
);



// WEBPACK FOOTER //
// ./src/js/components/common/select.jsx